/* eslint-disable no-magic-numbers -- canvas layout and floating-point tolerances */
import { UnreachableCaseError } from 'ts-essentials';
import type {
  CadTextFlowDirection,
  CadTextLineSpacingStyle,
  ITextObject,
  TextHorizontalAttachment,
} from './types/text-object';
import { splitMTextDisplayLines } from './content-format';

/** Matches {@link TrbMTextLayout.CadMTextLineSpacingNominal} in CivilModellingCommon. */
export const CAD_MTEXT_LINE_SPACING_NOMINAL = 1.66;
/** Matches {@link TrbMTextLayout.BaselineIncrementVisualScale}. */
export const BASELINE_INCREMENT_VISUAL_SCALE = 0.66;

export interface MTextLineLayout {
  readonly text: string;
  readonly canvasX: number;
  readonly canvasBaselineY: number;
  readonly widthPx: number;
}

export interface MTextBlockLayout {
  readonly lines: readonly MTextLineLayout[];
  readonly canvasWidth: number;
  readonly canvasHeight: number;
  readonly worldWidth: number;
  readonly worldHeight: number;
  readonly ascentPx: number;
  readonly descentPx: number;
}

export function getBaselineIncrementWorld(
  textHeight: number,
  lineSpacingFactor: number,
  lineSpacingStyle: CadTextLineSpacingStyle
): number {
  const factor = lineSpacingFactor > 1e-12 ? lineSpacingFactor : 1;
  const raw =
    lineSpacingStyle === 'exactly'
      ? textHeight * factor
      : textHeight * factor * CAD_MTEXT_LINE_SPACING_NOMINAL;
  return raw * BASELINE_INCREMENT_VISUAL_SCALE;
}

function stackDirection(drawDirection: CadTextFlowDirection): number {
  return drawDirection === 'bottomToTop' ? -1 : 1;
}

export function horizontalLineOffsetPx(
  lineWidthPx: number,
  blockWidthPx: number,
  attachment: TextHorizontalAttachment
): number {
  switch (attachment) {
    case 'left':
      return 0;
    case 'center':
      return (blockWidthPx - lineWidthPx) / 2;
    case 'right':
      return blockWidthPx - lineWidthPx;
    default:
      throw new UnreachableCaseError(attachment);
  }
}

export interface MeasureLineContext {
  measureWidth(text: string): number;
  measureAscentDescent(text: string): {
    readonly ascent: number;
    readonly descent: number;
  };
}

/**
 * Lays out text vertically (one character per row) for topToBottom / bottomToTop.
 * Each character is centered horizontally within the column.  For bottomToTop the
 * first character of the content appears at the bottom of the column.
 */
export function layoutVerticalMTextBlock(
  textObject: ITextObject,
  measure: MeasureLineContext
): MTextBlockLayout {
  const displayLines = splitMTextDisplayLines(textObject.content);
  const rawLines = displayLines.length > 0 ? displayLines : [''];

  const chars: string[] = [];
  for (const line of rawLines) {
    if (line.length === 0) {
      chars.push(' ');
    } else {
      for (const char of line) {
        chars.push(char);
      }
    }
  }
  if (chars.length === 0) {
    chars.push(' ');
  }

  let maxCharWidth = 0;
  let ascentPx = 0;
  let descentPx = 0;
  const charWidths: number[] = [];

  for (const char of chars) {
    const w = measure.measureWidth(char || ' ');
    const ad = measure.measureAscentDescent(char || ' ');
    charWidths.push(w);
    maxCharWidth = Math.max(maxCharWidth, w);
    ascentPx = Math.max(ascentPx, ad.ascent);
    descentPx = Math.max(descentPx, ad.descent);
  }

  const charCellPx = ascentPx + descentPx;
  const isBottomToTop = textObject.drawDirection === 'bottomToTop';

  const lineLayouts: MTextLineLayout[] = [];
  for (let i = 0; i < chars.length; i++) {
    const charWidth = charWidths[i] ?? 0;
    const canvasX = horizontalLineOffsetPx(
      charWidth,
      maxCharWidth,
      textObject.horizontalAttachment
    );

    const visualIndex = isBottomToTop ? chars.length - 1 - i : i;
    const canvasBaselineY = ascentPx + visualIndex * charCellPx;

    lineLayouts.push({
      text: chars[i] ?? '',
      canvasX,
      canvasBaselineY,
      widthPx: charWidth,
    });
  }

  const canvasWidth = maxCharWidth;
  const canvasHeight = Math.max(chars.length * charCellPx, charCellPx);

  const worldHeight = Math.max(textObject.textHeight, 1e-9);
  const pixelToWorld = worldHeight / Math.max(charCellPx, 1e-9);
  const worldBlockWidth = Math.max(
    canvasWidth * pixelToWorld,
    worldHeight * 0.01
  );
  const worldBlockHeight = Math.max(chars.length * worldHeight, worldHeight);

  return {
    lines: lineLayouts,
    canvasWidth,
    canvasHeight,
    worldWidth: worldBlockWidth,
    worldHeight: worldBlockHeight,
    ascentPx,
    descentPx,
  };
}

/**
 * Lays out MTEXT display lines on a canvas (Y-down) before world scaling.
 * Centers each line within the column width (max line width or box width), matching CAD MTEXT columns.
 */
export function layoutMTextBlock(
  textObject: ITextObject,
  canvasFontPx: number,
  measure: MeasureLineContext
): MTextBlockLayout {
  const displayLines = splitMTextDisplayLines(textObject.content);
  const lines = displayLines.length > 0 ? displayLines : [''];

  const baselineIncrementPx =
    (getBaselineIncrementWorld(
      textObject.textHeight,
      textObject.lineSpacingFactor,
      textObject.lineSpacingStyle
    ) /
      Math.max(textObject.textHeight, 1e-9)) *
    canvasFontPx;

  const lineWidths = lines.map((line) =>
    Math.max(measure.measureWidth(line || ' '), 0)
  );
  const maxLineWidthPx = Math.max(...lineWidths, 0);
  const boxWidthPx =
    textObject.boxWidth > 1e-9
      ? textObject.boxWidth *
        (canvasFontPx / Math.max(textObject.textHeight, 1e-9))
      : 0;
  const blockWidthPx = Math.max(maxLineWidthPx, boxWidthPx);

  const lineMetrics = lines.map((text) =>
    measure.measureAscentDescent(text || ' ')
  );
  const firstMetrics = lineMetrics[0] ?? measure.measureAscentDescent(' ');
  const ascentPx = Math.max(...lineMetrics.map((metrics) => metrics.ascent), 0);
  const descentPx = Math.max(
    ...lineMetrics.map((metrics) => metrics.descent),
    0
  );

  const direction = stackDirection(textObject.drawDirection);
  const lineLayouts: MTextLineLayout[] = [];

  for (let index = 0; index < lines.length; index++) {
    const text = lines[index] ?? '';
    const lineWidthPx = lineWidths[index] ?? 0;
    const canvasX = horizontalLineOffsetPx(
      lineWidthPx,
      blockWidthPx,
      textObject.horizontalAttachment
    );
    const stackOffset = index * baselineIncrementPx * direction;
    const canvasBaselineY = ascentPx + stackOffset;

    lineLayouts.push({
      text,
      canvasX,
      canvasBaselineY,
      widthPx: lineWidthPx,
    });
  }

  const lastLine = lineLayouts[lineLayouts.length - 1];
  const lastMetrics =
    lineMetrics[lineMetrics.length - 1] ??
    measure.measureAscentDescent(lastLine?.text || ' ');

  let contentTop: number;
  let contentBottom: number;

  if (direction >= 0) {
    contentTop = ascentPx - firstMetrics.ascent;
    contentBottom =
      (lastLine?.canvasBaselineY ?? ascentPx) + lastMetrics.descent;
  } else {
    contentTop = (lastLine?.canvasBaselineY ?? ascentPx) - lastMetrics.ascent;
    contentBottom =
      (lineLayouts[0]?.canvasBaselineY ?? ascentPx) + firstMetrics.descent;
  }

  const canvasHeight = Math.max(
    contentBottom - contentTop,
    ascentPx + descentPx
  );
  const canvasWidth = blockWidthPx;

  const normalizedLines = lineLayouts.map((line) => ({
    ...line,
    canvasBaselineY: line.canvasBaselineY - contentTop,
  }));

  const worldHeight = Math.max(textObject.textHeight, 1e-9);
  const pixelToWorld = worldHeight / Math.max(ascentPx + descentPx, 1e-9);
  const stackWorld =
    lines.length > 1
      ? (lines.length - 1) *
        getBaselineIncrementWorld(
          textObject.textHeight,
          textObject.lineSpacingFactor,
          textObject.lineSpacingStyle
        )
      : 0;
  const worldWidth = Math.max(blockWidthPx * pixelToWorld, worldHeight * 0.01);
  const worldBlockHeight = Math.max(
    worldHeight + (lines.length > 1 ? stackWorld : 0),
    worldHeight
  );

  return {
    lines: normalizedLines,
    canvasWidth,
    canvasHeight,
    worldWidth,
    worldHeight: worldBlockHeight,
    ascentPx,
    descentPx,
  };
}
